import type { Au13RuleSet } from "./types.js";

// AU-13 CCS ruleset for FY2025-26 (7 July 2025 – 30 June 2026).
//
// Figures transcribed VERBATIM from the DSS Family Assistance Guide:
//  - applicable percentage + income thresholds: §1.1.A.70
//  - hourly rate caps: §3.5.3 / §1.1.H.20
//  - subsidised-hours entitlement (3 Day Guarantee): Services Australia
//    "Activity level and subsidised hours" (post-5-Jan-2026 reform).
// Never edit a shipped ruleset to "fix" a later year — author a new FY file.
//
// Mid-year note: the 3 Day Guarantee took effect 5 Jan 2026, replacing the
// activity test partway through FY2025-26. We model the *current* (3DG) regime
// here because the live calculator and reclaim window both target the rules in
// force now; the activity-test FY2023-24/FY2024-25 rulesets cover the prior
// regime for backdated reclaims. If a within-FY2025-26 pre-5-Jan reclaim is
// ever needed, author a `2025-26:pre-3dg` period mirroring fy2024-25's hours.
export const fy2025_26: Au13RuleSet = {
  product: "AU13",
  fy: "2025-26",
  effectiveFrom: "2025-07-07",
  effectiveTo: "2026-06-30",
  periodKey: "2025-26",
  periodLabel: "FY2025-26 (3 Day Guarantee era)",
  periodFrom: "2025-07-07",
  periodTo: "2026-06-30",
  sources: {
    percentage: "https://guides.dss.gov.au/family-assistance-guide/1/1/a/70",
    incomeThresholds: "https://guides.dss.gov.au/family-assistance-guide/1/1/i/70",
    rateCaps: "https://guides.dss.gov.au/family-assistance-guide/3/5/3",
    hours:
      "https://www.servicesaustralia.gov.au/activity-level-and-subsidised-hours-for-child-care-subsidy",
  },
  sourcesVerifiedAt: "2026-06-13",
  // §1.1.A.70: 90% up to $85,279, taper 1% per $5,000, 0% at $535,279.
  standardTaper: {
    maxPercent: 0.9,
    maxThreshold: 85_279,
    taperStep: 5_000,
    zeroThreshold: 535_279,
  },
  // §1.1.A.70 higher CCS for second-and-younger children aged 5 or under:
  // 95% up to $143,273, taper $3,000 to 80% flat ($188,273–$267,563), taper
  // $3,000 to 50% flat ($357,563–$367,563), then standard rate at $367,563.
  higherRate: {
    topThreshold: 143_273,
    topPercent: 0.95,
    taperStep: 3_000,
    midPercent: 0.8,
    midFromThreshold: 188_273,
    midToThreshold: 267_563,
    floorPercent: 0.5,
    revertThreshold: 367_563,
  },
  // 3 Day Guarantee (from 5 Jan 2026): baseline 72 hrs/fortnight for every
  // eligible family; 100 hrs if each partner does 48+ activity hours/fortnight.
  hours: {
    mode: "three_day_guarantee",
    activityTiers: [],
    lowIncomeSafetyNetHours: 0,
    guaranteeHours: 72,
    highTierHours: 100,
    highTierActivityThreshold: 48,
    indigenousHours: 100,
  },
  // §3.5.3 hourly rate caps, 2025-26 (effective 7 July 2025).
  rateCaps: {
    centreBasedBelowSchool: 14.63,
    centreBasedSchoolAged: 12.81,
    oshcBelowSchool: 14.63,
    oshcSchoolAged: 12.81,
    familyDayCare: 13.56,
    inHomeCarePerFamily: 39.8,
  },
};
